import { MessageOutlined } from '@ant-design/icons';
import { UserRole } from '@cc/schema';
import { useCustom, useGetIdentity, useTranslate } from '@refinedev/core';
import { useActor } from '@xstate/react';
import {
  Alert,
  Button,
  Input,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { differenceInDays, format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClientName,
  ShowNamesButton,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { TherapistActivityReactContext } from '../../App';
import type { TherapistActivityService } from '../../machines/therapistActivityMachine';
import type { IUser } from '../../providers';
import { AssignHomeworkDrawer } from './AssignHomeworkDrawer';
import {
  AWAITING_BADGE_STYLE,
  BOARD_COMPARATORS,
  PRACTICE_BOARD_FIELDS,
  boardTherapistsOf,
  browserZone,
  scopeBoardRows,
  type BoardRow,
  type BoardSortKey,
} from './model';

/**
 * E34 · PracticeHomeworkBoard — "Practice — Homework board" (cc-homework, chapter "Practice — Homework
 * board (portal, E34)"): who is behind — one row per client of the practice (the Clients list's
 * scope: an admin sees the practice, a therapist their own), open · overdue · done this week ·
 * awaiting your reply · last activity · pending documents · the running program, overdue first.
 *
 * Behavior lines implemented here:
 *   · a row opens the client hub's Homework tab;
 *   · tick several rows → Assign homework… opens the Assign drawer for those clients: one
 *     AssignHomework per client (Q-e34-5 — no group entity); a refusal shows against its client;
 *   · awaiting your reply counts the client's assignments whose latest comment is their own — the client's
 *     OWN homework included, a cancelled row never (Q-board-1 A; the hub's count is the same number); the
 *     "Awaiting your reply" chip filters to those clients; the badge is soft amber (Q-badge-1 A);
 *   · Q-board-2 A — finding clients: a SEARCH box (any part of the first name, last name, "first last", e-mail or
 *     id — it matches the data, so it works while names are hidden), a THERAPIST filter for practice admins only,
 *     and SORTABLE columns (open · overdue · done this week · awaiting your reply · last activity · pending
 *     documents) — the default order stays "overdue first", the query's. All client-side over the loaded roster;
 *   · #3 — the header "Filter by client" (`TherapistActivityReactContext` → `filterClientId`) leaves only that
 *     client's row, BEFORE search and sort; clearing it restores the roster;
 *   · i18n homework.board.*.
 * The program reads the running pathway with its percentage (block 3a), linked to its progress; pending documents are the intake and consent
 * documents not yet submitted or signed (resolved from ClientWorksheets — block 2a).
 */
type BoardFilter = 'all' | 'behind' | 'awaitingReply' | 'quiet';
const FILTERS: BoardFilter[] = ['all', 'behind', 'awaitingReply', 'quiet'];

const QUIET_DAYS = 14;

const matches = (r: BoardRow, f: BoardFilter, now: Date): boolean => {
  switch (f) {
    case 'behind':
      return r.overdueCount > 0;
    case 'awaitingReply':
      return r.awaitingReply > 0;
    case 'quiet':
      return (
        !r.lastActivityAt ||
        differenceInDays(now, parseISO(r.lastActivityAt)) >= QUIET_DAYS
      );
    default:
      return true;
  }
};

export const PracticeHomeworkBoardPage = () => {
  const t = useTranslate();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<BoardFilter>('all');
  const [selected, setSelected] = useState<string[]>([]);
  const [assigning, setAssigning] = useState(false);
  // Q-mt-3 A: client names hidden by default, exactly like the Clients list; 60 s, never stored
  const names = useClientNamesVisibility();
  // Q-board-2 A: search and the admin's therapist filter; #3: the header's client filter
  const [search, setSearch] = useState('');
  const [therapist, setTherapist] = useState<string | undefined>();
  const { data: identity } = useGetIdentity<IUser>();
  const isAdmin = identity?.db?.role === UserRole.THERAPIST_ADMIN;
  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  const headerClientId = therapistActivityState?.context?.filterClientId || '';

  const { data, isFetching, isError, refetch } = useCustom<BoardRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'practiceHomeworkBoard',
      fields: PRACTICE_BOARD_FIELDS,
      variables: {
        timeZone: { name: 'timeZone', type: 'String', value: browserZone() },
      },
    },
  });
  const rows = useMemo(() => data?.data ?? [], [data]);
  const now = new Date();
  // the header's client, then the therapist, then the search — the tabs and their counts read what is left
  const scoped = useMemo(
    () =>
      scopeBoardRows(rows, {
        headerClientId,
        therapist: isAdmin ? therapist : undefined,
        search,
      }),
    [rows, headerClientId, therapist, isAdmin, search],
  );
  const therapists = useMemo(() => boardTherapistsOf(rows), [rows]);
  const shown = scoped.filter((r) => matches(r, filter, now));
  const narrowed = scoped.length !== rows.length;
  // a column's antd sorter (no column starts sorted: the query's "overdue first" is the default order)
  const sortable = (key: BoardSortKey) => ({
    sorter: BOARD_COMPARATORS[key],
    sortDirections: ['descend', 'ascend'] as ('descend' | 'ascend')[],
    onHeaderCell: () => ({ 'data-testid': `homework-board-sort-${key}` } as never),
  });
  // a person-reference key (users/…) is never shown in a name's place
  const nameOf = (r: BoardRow) => r.clientName || '';
  const dateLocale = i18n.languages[0]?.startsWith('ru') ? ru : enUS;
  const when = (iso: string | null | undefined) =>
    iso
      ? format(parseISO(iso), 'd MMM, HH:mm', { locale: dateLocale })
      : t('homework.board.none');
  const hubOf = (subject: string) =>
    `/clients/show/${encodeURIComponent(subject)}?tab=homework`;

  const count = (n: number, testid: string, danger = false) => (
    <Typography.Text
      type={danger && n > 0 ? 'danger' : undefined}
      strong={n > 0}
      data-testid={testid}
    >
      {n}
    </Typography.Text>
  );

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="homework-board"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Typography.Title level={3}>
            {t('homework.board.title')}
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            {t('homework.board.intro')}
          </Typography.Paragraph>
        </div>
        <ShowNamesButton
          visible={names.visible}
          onToggle={names.toggle}
          testId="homework-board-toggle-names"
        />
      </div>

      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Radio.Group
          optionType="button"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="homework-board-filter"
        >
          {FILTERS.map((f) => (
            <Radio.Button
              key={f}
              value={f}
              data-testid={`homework-board-filter-${f}`}
            >
              {t(`homework.board.filter.${f}`)} ·{' '}
              {scoped.filter((r) => matches(r, f, now)).length}
            </Radio.Button>
          ))}
        </Radio.Group>
        <Space>
          {selected.length > 0 ? (
            <Typography.Text
              type="secondary"
              data-testid="homework-board-selected"
            >
              {t('homework.board.selected', { count: selected.length })}
            </Typography.Text>
          ) : null}
          <Button
            type="primary"
            disabled={selected.length === 0}
            onClick={() => setAssigning(true)}
            data-testid="homework-board-assign"
          >
            {t('homework.board.assignSelected')}
          </Button>
        </Space>
      </Space>

      <Space wrap>
        <Input.Search
          allowClear
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => setSearch(v)}
          placeholder={t('homework.board.search')}
          data-testid="homework-board-search"
        />
        {isAdmin ? (
          // practice admins only — a therapist sees only their own clients and gets no filter
          <Select
            allowClear
            style={{ minWidth: 200 }}
            value={therapist}
            placeholder={t('homework.board.allTherapists')}
            aria-label={t('homework.board.therapistFilter')}
            options={therapists.map((n) => ({ value: n, label: n }))}
            onChange={(v) => setTherapist(v)}
            data-testid="homework-board-therapist-filter"
          />
        ) : null}
      </Space>

      {isError ? (
        <Alert type="error" showIcon message={t('homework.board.loadFailed')} />
      ) : null}

      <Table<BoardRow>
        rowKey="subject"
        loading={isFetching}
        dataSource={shown}
        pagination={false}
        size="small"
        tableLayout="fixed"
        scroll={{ x: 960 }}
        locale={{
          emptyText: t(
            narrowed || search.trim() ? 'homework.board.noMatch' : 'homework.board.empty',
          ),
        }}
        data-testid="homework-board-table"
        rowSelection={{
          selectedRowKeys: selected,
          onChange: (keys) => setSelected(keys as string[]),
          getCheckboxProps: (r) =>
            ({ 'data-testid': `homework-board-tick-${r.subject}` } as never),
        }}
        onRow={(r) =>
          ({
            'data-testid': `homework-board-row-${r.subject}`,
            'style': { cursor: 'pointer' },
            'onClick': (e: React.MouseEvent) => {
              // a tick is a selection, not an open
              const el = e.target as HTMLElement;
              if (
                el.closest(
                  '.ant-checkbox-wrapper, .ant-table-selection-column, a',
                )
              )
                return;
              navigate(hubOf(r.subject));
            },
          } as never)
        }
        columns={[
          {
            // the client, and their therapist of record in the practice under the name
            title: t('homework.board.column.client'),
            width: 158,
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Link
                  to={hubOf(r.subject)}
                  data-testid={`homework-board-client-${r.subject}`}
                >
                  <ClientName
                    id={r.subject}
                    name={nameOf(r)}
                    visible={names.visible}
                  />
                </Link>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12 }}
                  data-testid={`homework-board-therapist-${r.subject}`}
                >
                  {r.therapistName || t('homework.board.none')}
                </Typography.Text>
                {r.homeworkAppReady === false ? (
                  <Tooltip title={t('homework.board.appNotReadyWhy')}>
                    <Tag
                      color="gold"
                      style={{ marginTop: 2 }}
                      data-testid={`homework-board-app-not-ready-${r.subject}`}
                    >
                      {t('homework.board.appNotReady')}
                    </Tag>
                  </Tooltip>
                ) : null}
              </Space>
            ),
          },
          {
            title: t('homework.board.column.open'),
            ...sortable('open'),
            width: 108,
            render: (_, r) =>
              count(r.openCount, `homework-board-open-${r.subject}`),
          },
          {
            title: t('homework.board.column.overdue'),
            ...sortable('overdue'),
            width: 108,
            render: (_, r) =>
              count(
                r.overdueCount,
                `homework-board-overdue-${r.subject}`,
                true,
              ),
          },
          {
            title: t('homework.board.column.doneThisWeek'),
            ...sortable('doneThisWeek'),
            width: 108,
            render: (_, r) =>
              count(r.doneThisWeek, `homework-board-done-${r.subject}`),
          },
          {
            title: t('homework.board.column.awaitingReply'),
            ...sortable('awaitingReply'),
            width: 108,
            // Q-badge-1 A, compact on the board (founder 2026-10-03: the labelled pill spilled into Last activity on
            // a narrow screen or zoom): the column header already says "Awaiting your reply", so the cell is the
            // amber icon + the count; the full label is the tooltip and the accessible name. The hub keeps the label.
            render: (_, r) => {
              if (r.awaitingReply <= 0)
                return <span data-testid={`homework-board-awaiting-${r.subject}`} />;
              const label =
                r.awaitingReply > 1
                  ? t('homework.board.awaitingReplyBadgeCount', {
                      count: r.awaitingReply,
                    })
                  : t('homework.board.awaitingReplyBadge');
              return (
                <span data-testid={`homework-board-awaiting-${r.subject}`}>
                  <Tooltip title={label}>
                    <Tag
                      icon={<MessageOutlined />}
                      style={AWAITING_BADGE_STYLE}
                      aria-label={label}
                    >
                      {r.awaitingReply}
                    </Tag>
                  </Tooltip>
                </span>
              );
            },
          },
          {
            title: t('homework.board.column.lastActivity'),
            ...sortable('lastActivity'),
            width: 100,
            render: (_, r) => (
              <span data-testid={`homework-board-activity-${r.subject}`}>
                {when(r.lastActivityAt)}
              </span>
            ),
          },
          {
            title: t('homework.board.column.pendingDocuments'),
            ...sortable('pendingDocuments'),
            width: 120,
            // block 2a — the intake and consent documents not yet submitted or signed (ClientWorksheets)
            render: (_, r) => (
              <span data-testid={`homework-board-pending-${r.subject}`}>
                {r.pendingDocuments}
              </span>
            ),
          },
          {
            title: t('homework.board.column.pathway'),
            width: 112,
            // block 3a — the client's running pathway in the practice, opening its progress
            render: (_, r) =>
              r.pathwayName && r.enrollmentId ? (
                <Link
                  to={`/pathways/progress/${encodeURIComponent(
                    r.enrollmentId,
                  )}`}
                  data-testid={`homework-board-pathway-${r.subject}`}
                >
                  {`${r.pathwayName}${
                    r.pathwayPercentDone != null
                      ? ` · ${r.pathwayPercentDone}%`
                      : ''
                  }`}
                </Link>
              ) : (
                <span data-testid={`homework-board-pathway-${r.subject}`}>
                  {t('homework.board.none')}
                </span>
              ),
          },
        ]}
      />

      <AssignHomeworkDrawer
        open={assigning}
        namesVisible={names.visible}
        clients={rows
          .filter((r) => selected.includes(r.subject))
          .map((r) => ({
            subject: r.subject,
            name: nameOf(r),
            appReady: r.homeworkAppReady,
            lng: r.lng,
          }))}
        onClose={() => setAssigning(false)}
        onAssigned={(outcomes) => {
          if (outcomes.every((o) => o.ok)) setSelected([]);
          void refetch();
        }}
      />
    </Space>
  );
};
